import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Music2,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  Trash2,
  Download,
  Film,
  PlayCircle,
  Copy,
  Link2,
  ShoppingCart,
  Zap,
  Gamepad2,
  Bot,
  Inbox,
  Check,
  FolderUp,
  Radar as RadarIcon
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «TikTok» (solo admin): vídeos de ComparaCesta, Prisa o Ahorro y los juegos a los BORRADORES de TikTok.
// Solo /api/admin/tt/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/tt`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  planning: { label: "Buscando datos", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  uploading: { label: "Enviando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  ready: { label: "Listo, sin enviar", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  sent: { label: "En TikTok", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};
const TT_STATUS = {
  PROCESSING_UPLOAD: "Procesando en TikTok",
  PROCESSING_DOWNLOAD: "Procesando en TikTok",
  SEND_TO_USER_INBOX: "En tus borradores",
  PUBLISH_COMPLETE: "Publicado",
  FAILED: "Rechazado por TikTok"
};
const SOURCE_ICON = { comparacesta: ShoppingCart, prisa: Zap, juegos: Gamepad2, radar: RadarIcon };

const Copyable = ({ value, label }) => {
  const [done, setDone] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 min-w-0 truncate bg-black/50 border border-white/10 rounded px-2 py-1 text-[11px] text-pink-200">{value}</code>
      <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 2000); }}
        className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 shrink-0">
        <Copy className="w-3 h-3" /> {done ? "Copiado" : label || "Copiar"}
      </Button>
    </div>
  );
};

export const TikTokView = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [appCreds, setAppCreds] = useState({ client_key: "", client_secret: "" });
  const [game, setGame] = useState("");
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const timer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account`, { params: { origin: window.location.origin }, ...opts() });
      setStatus(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar la conexión con TikTok." });
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config`, opts());
      setConfig(res.data);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadVideos();
    const onMsg = (ev) => {
      if (ev.data?.type === "TT_CONNECTED") {
        setWorking(null);
        setFeedback({ type: "success", text: ev.data.message || "TikTok conectado." });
        loadStatus();
      } else if (ev.data?.type === "TT_ERROR") {
        setWorking(null);
        setFeedback({ type: "error", text: ev.data.message || "TikTok rechazó la conexión." });
        loadStatus();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [loadStatus, loadConfig, loadVideos]);

  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy ||
    (v.tt_status && v.tt_status.startsWith("PROCESSING") && Date.now() - new Date(v.sent_at).getTime() < 3600 * 1000));
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadStatus(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadStatus]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      const msg = await fn();
      if (okText || msg) setFeedback({ type: "success", text: msg || okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const saveApp = () => run("app", async () => {
    const res = await axios.post(`${API}/account/app`, appCreds, opts());
    setStatus(res.data);
    setAppCreds({ client_key: "", client_secret: "" });
  }, "App de TikTok guardada. Ahora pulsa «Conectar con TikTok».");

  const connect = async () => {
    setFeedback(null);
    setWorking("connect");
    try {
      const res = await axios.get(`${API}/account/auth-url`, { params: { origin: window.location.origin }, ...opts() });
      const popup = window.open(res.data.auth_url, "cortoviral_tt_oauth", "width=520,height=780,left=200,top=80,resizable=yes,scrollbars=yes");
      if (!popup) {
        window.location.href = res.data.auth_url;
        return;
      }
      setFeedback({ type: "info", text: "En la ventana de TikTok entra con tu cuenta y acepta los permisos. Al terminar, esta pantalla se actualiza sola." });
      const t = setInterval(() => {
        if (popup.closed) {
          clearInterval(t);
          setWorking(null);
          loadStatus();
        }
      }, 800);
    } catch (e) {
      setWorking(null);
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error iniciando la conexión con TikTok." });
    }
  };

  const disconnect = () => {
    if (!window.confirm("¿Desconectar la cuenta de TikTok? La app guardada se conserva para volver a conectar.")) return;
    run("disconnect", async () => {
      const res = await axios.post(`${API}/account/disconnect`, {}, opts());
      setStatus(res.data);
    }, "Cuenta de TikTok desconectada.");
  };

  const forgetApp = () => {
    if (!window.confirm("¿Borrar también el Client key y el Client secret guardados?")) return;
    run("forget", async () => {
      const res = await axios.post(`${API}/account/forget-app`, {}, opts());
      setStatus(res.data);
    }, "App de TikTok borrada.");
  };

  const saveConfig = (patch) => run("config", async () => {
    const res = await axios.post(`${API}/config`, patch, opts());
    setConfig(res.data);
  });

  const toggleIn = (list, item) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const createVideo = (source, mode) => run(`create-${source}-${mode || ""}`, async () => {
    await axios.post(`${API}/videos/create`, { source, mode, upload: !!status?.connected }, opts());
    setBusy(true);
    loadVideos();
  }, status?.connected
    ? "Creando el vídeo. En un par de minutos llegará a tus borradores de TikTok."
    : "Creando el vídeo. Como TikTok no está conectado, se quedará aquí para descargarlo.");

  const uploadVideo = (v) => run(`up-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/upload`, {}, opts());
    loadVideos();
    loadStatus();
  }, "Enviado. En unos minutos te llegará una notificación de TikTok para terminarlo en el móvil.");

  const markPosted = (v) => run(`posted-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/posted`, {}, opts());
    loadVideos();
  });

  const refreshStatus = () => run("refresh", async () => {
    const res = await axios.post(`${API}/videos/refresh-status`, {}, opts());
    setVideos(res.data.videos);
  });

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en TikTok, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  const copyCaption = (v) => {
    navigator.clipboard.writeText(v.caption || "");
    setCopiedId(v.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const uploadClip = (g, file) => {
    if (!file) return;
    run(`clip-${g}`, async () => {
      setUploadPct(0);
      try {
        const res = await axios.post(`${API}/material/${g}/clip`, file, {
          ...opts(),
          params: { name: file.name },
          headers: { ...authHeaders(), "Content-Type": "application/octet-stream" },
          onUploadProgress: (ev) => ev.total && setUploadPct(Math.round((ev.loaded / ev.total) * 100))
        });
        setConfig((c) => ({ ...c, material: res.data.material }));
        return `Clip guardado (${String(res.data.duration).replace(".", ",")} s). Se usará en los próximos vídeos.`;
      } finally {
        setUploadPct(null);
      }
    });
  };

  const deleteClip = (g, name) => {
    if (!window.confirm(`¿Borrar el clip ${name}?`)) return;
    run(`delclip-${g}`, async () => {
      const res = await axios.delete(`${API}/material/${g}/clip/${encodeURIComponent(name)}`, opts());
      setConfig((c) => ({ ...c, material: res.data.material }));
    });
  };

  if (!status || !config) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando TikTok...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = config.settings;
  const material = config.material;
  const gamesWithMaterial = Object.entries(material).filter(([, m]) => m.ok);
  const fbStyle = feedback?.type === "success"
    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
    : feedback?.type === "info" ? "bg-blue-500/10 border border-blue-500/30 text-blue-200"
      : "bg-red-500/10 border border-red-500/30 text-red-300";
  const card = "bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3";
  const chip = (on) => `px-2.5 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition-all ${
    on ? "bg-pink-600/20 border-pink-500/50 text-pink-200" : "bg-black/30 border-white/10 text-slate-400 hover:text-white"}`;

  return (
    <div data-testid="tt-view" className="space-y-6">
      {/* Conexión */}
      <div className="bg-gradient-to-br from-[#12141D] to-[#2A0F1E] border border-pink-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {status.connected && status.avatar_url ? (
              <img src={status.avatar_url} alt="" className="w-12 h-12 rounded-full border border-pink-500/40 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-pink-500/15 border border-pink-500/40 text-pink-300 flex items-center justify-center shrink-0">
                <Music2 className="w-6 h-6" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">TikTok</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/30">Solo admin</span>
                {status.connected ? (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                    Cuenta: {status.display_name || "conectada"}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Vídeos con datos reales de ComparaCesta, Prisa o Ahorro y tus juegos. Llegan a los <strong className="text-slate-200">borradores</strong> de
                tu TikTok: tú les pones un sonido en tendencia, pegas el texto y publicas desde el móvil. Va aparte de YouTube y no gasta créditos.
              </p>
            </div>
          </div>
          {status.connected && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">Enviados en 24 h: {status.uploads_today}/{status.max_uploads}</span>
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${fbStyle}`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}
        {status.last_error && <p className="text-[11px] text-red-400 break-words">Último error: {status.last_error}</p>}

        {!status.connected && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2 min-w-0">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-pink-300" /> App de TikTok for Developers</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>En <strong className="text-slate-200">developers.tiktok.com</strong> crea una app (tipo web) y añade los productos <strong className="text-slate-200">Login Kit</strong> y <strong className="text-slate-200">Content Posting API</strong> (sin «Direct Post»).</li>
                <li>Permisos: {status.scopes.join(", ")}.</li>
                <li>En Login Kit, pon esta Redirect URI:</li>
              </ol>
              <Copyable value={status.redirect_uri} />
              <p className="text-slate-400">4. Las páginas legales que pide la app:</p>
              <Copyable value={status.site_urls.terms} label="Condiciones" />
              <Copyable value={status.site_urls.privacy} label="Privacidad" />
              <p className="text-slate-400">5. Pega aquí el Client key y el Client secret y pulsa «Conectar con TikTok».</p>
              <p className="text-slate-500">Mientras TikTok revisa la app puedes probar con un Sandbox (con tu cuenta como «target user»): tiene su propio Client key.</p>
            </div>
            <div className="space-y-3">
              {status.has_app ? (
                <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2">
                  <p className="break-all">App de TikTok guardada: <code className="text-pink-200">{status.client_key}</code></p>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="tt-connect-btn" onClick={connect} disabled={working !== null}
                      className="bg-pink-600 hover:bg-pink-500 text-white font-black text-xs flex items-center gap-1.5">
                      {working === "connect" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                      Conectar con TikTok
                    </Button>
                    <Button variant="ghost" size="sm" onClick={forgetApp} disabled={working !== null}
                      className="h-9 text-xs text-slate-400 hover:text-red-300">Cambiar app</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <input type="text" placeholder="Client key" value={appCreds.client_key} autoComplete="off"
                    onChange={(e) => setAppCreds((c) => ({ ...c, client_key: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-pink-500/60" />
                  <input type="password" placeholder="Client secret" value={appCreds.client_secret} autoComplete="new-password"
                    onChange={(e) => setAppCreds((c) => ({ ...c, client_secret: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-pink-500/60" />
                  <Button data-testid="tt-save-app-btn" onClick={saveApp} disabled={working !== null || !appCreds.client_key || !appCreds.client_secret}
                    className="w-full bg-pink-600 hover:bg-pink-500 text-white font-black text-xs flex items-center justify-center gap-1.5">
                    {working === "app" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Guardar app de TikTok
                  </Button>
                  <p className="text-[10px] text-slate-500">Se guardan solo en el servidor y nunca se devuelven al navegador.</p>
                </div>
              )}
              <p className="text-[11px] text-slate-500">
                Sin conectar también puedes crear vídeos: se quedan aquí para verlos y descargarlos.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Crear ahora */}
      <div className={card}>
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Film className="w-4 h-4 text-pink-300" /> Crear un vídeo ahora</h4>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => createVideo("comparacesta", "producto")} disabled={working !== null || busy}
            className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: un producto
          </Button>
          <Button onClick={() => createVideo("comparacesta", "cesta")} disabled={working !== null || busy}
            className="bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: una cesta
          </Button>
          <Button onClick={() => createVideo("prisa")} disabled={working !== null || busy}
            className="bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Prisa o Ahorro: un chollo
          </Button>
          <div className="flex items-center gap-1.5">
            <select value={game} onChange={(e) => setGame(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white">
              <option value="">Juego (el siguiente)</option>
              {gamesWithMaterial.map(([g, m]) => <option key={g} value={g}>{m.name}</option>)}
            </select>
            <Button onClick={() => createVideo("juegos", game || undefined)} disabled={working !== null || busy || gamesWithMaterial.length === 0}
              className="bg-violet-600 hover:bg-violet-500 text-white font-bold text-xs flex items-center gap-1.5">
              <Gamepad2 className="w-3.5 h-3.5" /> Clip de juego
            </Button>
          </div>
          <Button onClick={() => createVideo("radar")} disabled={working !== null || busy || !config.radar.fresh}
            title={config.radar.fresh ? "" : "El Radar Viral no tiene temas nuevos de las últimas 72 h"}
            className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs flex items-center gap-1.5">
            <RadarIcon className="w-3.5 h-3.5" /> Radar Viral: un tema ({config.radar.fresh})
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">
          Se hace uno a la vez. Los precios salen en directo de {config.sites.comparacesta.replace("https://", "")} y {config.sites.prisa.replace("https://", "")}.
          Los del Radar Viral usan sus temas de las últimas 72 h (solo los lee): la IA escribe un guion nuevo con fotos de stock.
          {busy && " Ahora mismo se está creando uno."}
        </p>
        {!config.radar.main_enabled && (
          <p className="text-[11px] text-amber-300">
            El Radar Viral está apagado: no encontrará temas nuevos. Los que ya tiene siguen sirviendo hasta que pasen 72 h.
          </p>
        )}
      </div>

      {/* Piloto */}
      <div className={card}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Bot className="w-4 h-4 text-pink-300" /> Piloto automático</h4>
          <button onClick={() => saveConfig({ autopilot_enabled: !s.autopilot_enabled })} disabled={working !== null}
            className={`px-3 py-1.5 rounded-lg text-xs font-black border ${s.autopilot_enabled
              ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "bg-slate-800 border-white/10 text-slate-400"}`}>
            {s.autopilot_enabled ? "ENCENDIDO" : "APAGADO"}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Crea vídeos solo, turnándose entre las fuentes activadas, y los manda a tus borradores. TikTok admite como mucho {config.max_uploads} borradores
          pendientes en 24 h.
          {s.autopilot_enabled && config.next_autopilot && <> Próximo: <strong className="text-slate-200">{new Date(config.next_autopilot).toLocaleString("es-ES")}</strong>.</>}
        </p>
        {s.last_error && <p className="text-[11px] text-red-400 break-words">{s.last_error}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
          <div className="space-y-2">
            <p className="text-slate-300 font-bold">Vídeos al día</p>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].slice(0, config.max_uploads).map((n) => (
                <button key={n} onClick={() => saveConfig({ videos_per_day: n })} disabled={working !== null} className={chip(s.videos_per_day === n)}>{n}</button>
              ))}
            </div>
            <p className="text-slate-300 font-bold pt-1">Fuentes</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(config.source_names).map(([k, label]) => {
                const Icon = SOURCE_ICON[k] || Film;
                return (
                  <button key={k} onClick={() => saveConfig({ sources: toggleIn(s.sources || [], k) })} disabled={working !== null} className={chip((s.sources || []).includes(k))}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-slate-300 pt-1">
              <input type="checkbox" checked={!!s.auto_upload} onChange={(e) => saveConfig({ auto_upload: e.target.checked })} className="accent-pink-500 w-4 h-4" />
              Enviar a mis borradores de TikTok al terminar
            </label>
            <label className="flex items-center gap-2 text-slate-300">
              Código postal para los precios
              <input type="text" inputMode="numeric" maxLength={5} placeholder="(ninguno)" defaultValue={s.cc_postal_code}
                onBlur={(e) => e.target.value !== s.cc_postal_code && saveConfig({ cc_postal_code: e.target.value })}
                className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-white" />
            </label>
          </div>
          <div className="space-y-2">
            <p className="text-slate-300 font-bold">Juegos que entran en el turno</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(material).map(([g, m]) => (
                <button key={g} onClick={() => saveConfig({ games: toggleIn(s.games || [], g) })} disabled={working !== null}
                  className={chip((s.games || []).includes(g))} title={m.ok ? "" : "Sin material todavía"}>
                  {m.name}{!m.deployed && <span className="text-[9px] text-amber-300">(sin desplegar)</span>}{!m.ok && <span className="text-[9px] text-red-300">(sin material)</span>}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500">Los juegos que aún no están desplegados vienen apagados: actívalos cuando su bot funcione.</p>
          </div>
        </div>
      </div>

      {/* Material */}
      <div className={card}>
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><FolderUp className="w-4 h-4 text-pink-300" /> Material de los juegos</h4>
        <p className="text-xs text-slate-400">
          Cada vídeo de juego mezcla tramos de partida con el final de «Jugar en Telegram». Viene con el vídeo promo y las capturas de cada juego;
          si subes clips de tus partidas (MP4 vertical, grabados en el móvil), los vídeos variarán mucho más.
        </p>
        <div className="space-y-1.5">
          {Object.entries(material).map(([g, m]) => (
            <div key={g} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-black/30 border border-white/5 rounded-lg px-3 py-2 text-xs min-w-0">
              <div className="min-w-0">
                <span className="font-bold text-white">{m.name}</span>{" "}
                <span className="text-slate-500">@{m.bot}</span>
                <span className={`ml-2 ${m.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {m.ok ? `${m.windows} tramos · ${m.images} capturas${m.cta ? " · final con el bot" : ""}` : "sin material"}
                </span>
                {m.extras.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.extras.map((name) => (
                      <span key={name} className="inline-flex items-center gap-1 bg-slate-800 rounded px-1.5 py-0.5 text-[10px] text-slate-300">
                        {name}
                        <button onClick={() => deleteClip(g, name)} disabled={working !== null} className="text-slate-500 hover:text-red-300"><Trash2 className="w-2.5 h-2.5" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <label className={`shrink-0 cursor-pointer inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 ${working !== null ? "opacity-50 pointer-events-none" : ""}`}>
                {working === `clip-${g}` ? <><RefreshCw className="w-3 h-3 animate-spin" /> {uploadPct ?? 0} %</> : <><Upload className="w-3 h-3" /> Subir clip</>}
                <input type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={(e) => { uploadClip(g, e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Vídeos */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white">Vídeos de TikTok ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={status.connected ? refreshStatus : loadVideos} disabled={working !== null}
            className="h-7 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${anyBusy || working === "refresh" ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
        <div className="p-3 rounded-xl bg-pink-500/10 border border-pink-500/20 text-[11px] text-pink-100 flex items-start gap-2">
          <Inbox className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            En el móvil: abre la notificación de TikTok (o Perfil → Borradores/Bandeja), añade un <strong>sonido en tendencia</strong> (baja el volumen del original),
            pega el texto con «Copiar texto» y publica. Si TikTok pregunta, marca «Tu marca»: promocionas tu propia web o juego.
          </span>
        </div>
        {videos.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            Todavía no hay vídeos. Crea uno arriba o enciende el piloto automático.
          </div>
        ) : (
          <div className="space-y-2">
            {videos.map((v) => {
              const st = STATUS[v.status] || STATUS.failed;
              const fileUrl = `${API}/videos/${v.id}/file`;
              const hasFile = v.video_url && !v.video_deleted;
              const Icon = SOURCE_ICON[v.source] || Film;
              const failedTT = v.tt_status === "FAILED";
              return (
                <div key={v.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 space-y-2 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${st.style}`}>
                          {st.busy && <RefreshCw className="w-2.5 h-2.5 inline mr-1 animate-spin" />}{st.label}
                        </span>
                        {v.status === "sent" && v.tt_status && (
                          <span className={`text-[10px] uppercase font-bold ${failedTT ? "text-red-400" : "text-pink-300"}`}>{TT_STATUS[v.tt_status] || v.tt_status}</span>
                        )}
                        {v.posted && <span className="text-[10px] uppercase font-bold text-emerald-300">Publicado (marcado)</span>}
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                          <Icon className="w-3 h-3" /> {config.source_names[v.source] || v.source} · {new Date(v.created_at).toLocaleString("es-ES")}
                          {v.duration ? ` · ${String(v.duration).replace(".", ",")} s` : ""}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-white break-words">{v.title}</p>
                      {v.progress && <p className={`text-[11px] break-words ${v.status === "failed" || failedTT ? "text-red-400" : "text-slate-400"}`}>{v.progress}</p>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {v.caption && (
                        <Button size="sm" onClick={() => copyCaption(v)}
                          className="h-7 px-2.5 text-[11px] font-bold bg-slate-100 hover:bg-white text-slate-950 flex items-center gap-1">
                          {copiedId === v.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copiedId === v.id ? "Copiado" : "Copiar texto"}
                        </Button>
                      )}
                      {hasFile && (
                        <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)}
                          className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <PlayCircle className="w-3 h-3" /> {preview === v.id ? "Ocultar" : "Ver"}
                        </Button>
                      )}
                      {hasFile && (
                        <a href={fileUrl} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 rounded-md hover:bg-white/5">
                          <Download className="w-3 h-3" /> Descargar
                        </a>
                      )}
                      {v.status === "ready" && hasFile && (
                        <Button size="sm" onClick={() => uploadVideo(v)} disabled={working !== null || !status.connected}
                          title={status.connected ? "" : "Conecta tu cuenta de TikTok"}
                          className="h-7 px-2.5 text-[11px] font-bold bg-pink-600 hover:bg-pink-500 text-white flex items-center gap-1">
                          {working === `up-${v.id}` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          Enviar a TikTok
                        </Button>
                      )}
                      {v.status === "sent" && !failedTT && (
                        <Button size="sm" variant="ghost" onClick={() => markPosted(v)} disabled={working !== null}
                          className={`h-7 px-2 text-[11px] flex items-center gap-1 ${v.posted ? "text-emerald-300" : "text-slate-400 hover:text-white"}`}>
                          <CheckCircle2 className="w-3 h-3" /> {v.posted ? "Publicado" : "Marcar publicado"}
                        </Button>
                      )}
                      {!st.busy && (
                        <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null}
                          className="h-7 px-2 text-[11px] text-slate-500 hover:text-red-300 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {preview === v.id && hasFile && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <video src={fileUrl} controls className="w-full max-w-[280px] aspect-[9/16] rounded-lg bg-black" />
                      {v.caption && <pre className="flex-1 min-w-0 whitespace-pre-wrap break-words text-[11px] text-slate-300 bg-black/30 border border-white/5 rounded-lg p-3 font-sans">{v.caption}</pre>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
